/** 已经声明/定义的数据 */
export function isDef<T>(val: T): val is NonNullable<T> {
  return val !== undefined && val !== null;
}

// 检查是否是 ie 浏览器
export function checkIeBrowser(notLowerThanVerison?: number) {
  const userAgent = navigator.userAgent;
  // 判断是否IE<11浏览器
  const isIE = userAgent.indexOf('compatible') > -1 && userAgent.indexOf('MSIE') > -1;
  // 判断是否IE11浏览器
  const isIE11 = userAgent.indexOf('Trident') > -1 && userAgent.indexOf('rv:11.0') > -1;
  // 判断是否为 Edge 浏览器
  const isEdge = userAgent.indexOf('Edge') > -1 && !isIE;
  if (notLowerThanVerison) {
    if (notLowerThanVerison > 11) {
      return isEdge;
    }
    if (notLowerThanVerison === 11) {
      return isEdge || isIE11;
    }
  }
  return isIE || isIE11 || isEdge;
}

export function findLabelInList<T extends { label: string; value: any }>(list: T[], key: any) {
  const value = list.filter((item) => item.value === key);
  if (value.length) {
    return value[0].label;
  }
  return '--';
}

export const findNodeById = (
  nodes: any[],
  id: string,
  idName = 'id',
  childrenName = 'child',
): any => {
  if (!nodes?.length) return null;
  for (const node of nodes) {
    if (node[idName] === id) {
      return node;
    }
    if (node[childrenName]) {
      const foundNode = findNodeById(node[childrenName], id, idName, childrenName);
      if (foundNode) {
        return foundNode;
      }
    }
  }
  return null;
};

/**
 * 转换为千分位数字格式
 * @param val 数值
 * @param defaultStr 如果没有，默认展示的值
 */
export const thousandNumber = (
  val: string | number | undefined | null,
  defaultStr = '--',
): string => {
  if (isDef(val)) {
    let numberVal = val;
    if (typeof numberVal === 'string' && val !== '') {
      numberVal = Number(val);
    }
    if (Number.isNaN(val)) {
      return `${val ?? defaultStr}`;
    }
    const stringVal = numberVal?.toString();
    const [intStr, decimalStr] = stringVal.split('.');
    return `${intStr.replace(/(\d)(?=(\d{3})+(?!\d))/g, (n) => `${n},`)}${
      decimalStr ? `.${decimalStr}` : ''
    }`;
  } else {
    return `${val ?? defaultStr}`;
  }
};

/**
 * @desc 判断是否是JSON对象
 * @param str
 */
export const isJSONObject = (str: unknown): boolean => {
  if (typeof str !== 'string') return false;
  try {
    const parsed = JSON.parse(str);
    return typeof parsed === 'object' && !Array.isArray(parsed) && parsed !== null;
  } catch {
    return false;
  }
};

/**
 * @desc 是否是指定环境
 */
export const isOrigin = () => {
  const origin = window.location.origin || '';

  return [
    'https://carserve-admin.yucunkeji.com',
    'https://drivers-admin.hwpre.yucunkeji.com',
    'http://localhost:3000',
  ].includes(origin);
};

/**
 * 添加千分位
 * 去掉小数点
 */
export const formatInt = (val: any) => {
  const num = Number(val);
  if (isNaN(num)) return '';
  return Math.trunc(num).toLocaleString();
};

/**
 * @param {string} filename 原始文件名，例如 "名单导入模板.20251031 (1).xlsx"
 * @param {number} maxChinese 最大汉字数，默认 10
 * @returns {string} 格式化后的展示名，例如 "名单导入模板.20251031 (1).xlsx" 或 "很长的文件名前缀...xlsx"
 */
export function formatFilenameDisplay(filename: string, maxChinese = 10) {
  if (!filename || typeof filename !== 'string') return '';

  // 找到最后一个点，区分扩展名
  const lastDot = filename.lastIndexOf('.');
  const ext = lastDot === -1 ? '' : filename.slice(lastDot + 1);
  const base = lastDot === -1 ? filename : filename.slice(0, lastDot);

  // 遍历 base，当汉字计数超过 maxChinese 时截断并加省略号
  // 使用 Unicode 汉字检测（需要支持 u 标志）
  const isHan = (ch: string) => /\p{Script=Han}/u.test(ch);

  let chineseCount = 0;
  let display = '';
  let exceeded = false;

  for (const ch of base) {
    if (isHan(ch)) {
      if (chineseCount >= maxChinese) {
        exceeded = true;
        break;
      }
      chineseCount++;
      display += ch;
    } else {
      // 非汉字直接保留
      display += ch;
    }
  }

  if (exceeded) display = display + '...';

  return ext ? `${display}.${ext}` : display;
}

/**
 * 将 items 剪裁到指定最大深度（根为 level=1）。
 * @param {Array} items 原始数组
 * @param {number} maxDepth 最大层级（默认 3）
 * @returns {Array} 新数组，超过 maxDepth 的 child 将被删除
 */
export function trimToDepth(items: Array<any>, maxDepth = 3): Array<any> {
  if (!Array.isArray(items)) return [];

  function helper(node: { [x: string]: any; child: any }, depth: number) {
    // 复制除 child 之外的其它字段（保持原有字段）
    const { child, ...rest } = node;
    const out = { ...rest };

    // 只有在当前 depth < maxDepth 时，才继续处理 child
    if (Array.isArray(child) && depth < maxDepth) {
      out.child = child.map((c) => helper(c, depth + 1));
    }
    // 当 depth >= maxDepth 时，不添加 child 字段（即删除第四层及更深）
    return out;
  }

  return items.map((item) => helper(item, 1));
}

/**
 * 获取当前后端域名（用于 getBackendStyle 等接口）
 * - 开发环境固定走 https://car-admin.5i5s.cn
 * - 生产环境取 window.location.origin 并去掉端口号
 */
export function getBackendDomain(): string {
  const url = window.location.origin;
  return process.env.NODE_ENV === 'development' ? 'https://car-admin.5i5s.cn' : url;
}

/**
 * 处理图片 URL 格式转换（统一函数）
 * - 当 imgUrl 是数组时，取第一个元素的 url 字段
 * - 当 imgUrl 是字符串时，转换成 [{url: imgUrl}] 格式
 * @param {string | Array<{url: string}>} imgUrl - 图片 URL
 * @returns {string | Array<{url: string}>} - 数组转字符串，字符串转数组
 */
export const handleImgUrl = (imgUrl: string | Array<{ url: string }>) => {
  // if (!imgUrl) return [];
  if (Array.isArray(imgUrl)) {
    // 数组转字符串：取第一个元素的 url
    return imgUrl?.[0]?.url ?? '';
  }

  // 字符串转数组：[{url: string}]
  if (typeof imgUrl === 'string' && imgUrl) {
    return [{ url: imgUrl }];
  }
  return null;
};
